import { styled } from 'styled-components'

import { HolderTableBalanceCell } from '../ui/holder-table-balance-cell'
import { HolderTableBalanceHeader } from '../ui/holder-table-balance-header'
import { HolderTableBoughtCell } from '../ui/holder-table-bought-cell'
import { HolderTableLastActiveCell } from '../ui/holder-table-last-active-cell'
import { HolderTableMakerCell } from '../ui/holder-table-maker-cell'
import { HolderTableMakerHeader } from '../ui/holder-table-maker-header'
import { HolderTablePnlCell } from '../ui/holder-table-pnl-cell'
import { HolderTableRankCell } from '../ui/holder-table-rank-cell'
import { HolderTableRemainingCell } from '../ui/holder-table-remaining-cell'
import { HolderTableSoldCell } from '../ui/holder-table-sell-cell'

import type { PairHoldersTableColumnId } from './reatom-pair-holders-column-id'
import type { PairAddress, PairModel } from '@root/entities/pair'
import type { Maker } from '@root/entities/trade'
import type { ChildrenProps } from '@root/shared/react'
import type { Column } from '@root/shared/ui-kit'

export const createPairHoldersTableColumns = (options: {
  pair: PairModel | undefined
}) => {
  const columns: Array<
    Column<
      Maker.RankedMaker,
      PairHoldersTableColumnId,
      { pairAddress: PairAddress }
    >
  > = [
    {
      id: 'rank',
      render: ({ value }) => (
        <HolderTableRankCell rank={value.rank}></HolderTableRankCell>
      ),
      header: ({ t }) => t('table.rank'),
      getValue: (x) => x.rank,
      width: 40,
    },
    {
      id: 'maker',
      render: (dataTableCell) => (
        <HolderTableMakerCell
          dataTableCell={dataTableCell}
          pair={options.pair}
        />
      ),

      header: ({ filter, setColumnFilter, clearColumnFilter, columnId }) => (
        <HolderTableMakerHeader
          filter={filter}
          setColumnFilter={setColumnFilter}
          clearColumnFilter={clearColumnFilter}
          columnId={columnId}
        />
      ),
      getValue: (x) => x.address,
      minWidth: 165,
      filterable: true,
    },
    {
      id: 'sol-bal',
      render: ({ value }) => (
        <HolderTableBalanceCell balance={value.balanceInNative} />
      ),
      header: () => <HolderTableBalanceHeader />,
      getValue: (x) => x.balanceInNative,
      minWidth: 100,
    },
    {
      id: 'bought',
      render: ({ value }) => (
        <CheckForLiquidityPool
          pairAddress={options.pair?.address}
          maker={value}
        >
          <HolderTableBoughtCell
            buys={value.buys}
            buyVolumeInUsd={value.buyVolumeInUsd}
            buyVolumeInToken={value.buyVolumeCurrency}
          />
        </CheckForLiquidityPool>
      ),
      header: ({ t }) => t('table.bought'),
      minWidth: 100,
    },
    {
      id: 'sold',
      render: ({ value }) => (
        <CheckForLiquidityPool
          pairAddress={options.pair?.address}
          maker={value}
        >
          <HolderTableSoldCell
            sells={value.sells}
            sellVolumeInToken={value.sellVolumeCurrency}
            sellVolumeInUsd={value.sellVolumeInUsd}
          />
        </CheckForLiquidityPool>
      ),
      header: ({ t }) => t('table.sold'),
      minWidth: 100,
    },
    {
      id: 'pnl',
      render: ({ value }) => (
        <CheckForLiquidityPool
          pairAddress={options.pair?.address}
          maker={value}
        >
          <HolderTablePnlCell pnl={value.pnl} />
        </CheckForLiquidityPool>
      ),
      header: ({ t }) => t('table.pnl'),
      getValue: (x) => x.pnl,
      minWidth: 100,
    },
    {
      id: 'remaining',
      render: ({ value }) => (
        <HolderTableRemainingCell
          balanceInUsd={value.balanceInUsd}
          balanceInUsdPercentage={value.balanceInUsdPercentage}
        />
      ),
      header: ({ t }) => t('table.remaining'),
      getValue: (x) => x.address,
      minWidth: 100,
      justify: 'stretch',
    },
    {
      id: 'active',
      render: ({ value }) => (
        <HolderTableLastActiveCell lastActiveAt={value.lastTradeAt} />
      ),
      header: ({ t }) => t('table.active'),
      getValue: (x) => x.lastTradeAt,
      width: 160,
      justify: 'center',
    },
  ]

  return columns
}

const CheckForLiquidityPool = ({
  pairAddress,
  maker,
  children,
}: {
  pairAddress: PairAddress | undefined
  maker: Maker.MakerShort
} & ChildrenProps) => {
  if (
    pairAddress &&
    maker.address.toLowerCase() === pairAddress.toLowerCase()
  ) {
    return <Hyphen>-</Hyphen>
  }
  return children
}

const Hyphen = styled.span`
  color: var(--text-9);
`
